<template>
<div class="chat-results-container">
    <DxDataGrid 
        :show-borders="true" 
        :data-source="dataSource" 
        ref="gridComponent"
        :column-auto-width="true"
        :allow-column-resizing="true"
        column-resizing-mode="widget"
        :row-alternation-enabled="true"
        @exporting="onExporting"
    >
        <DxPaging :enabled="true" :page-size="10" />
        <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 25, 50]" :show-info="true" />
        <DxFilterRow :visible="true" />
        <DxHeaderFilter :visible="true" />
        <DxSearchPanel :visible="true" :width="250" />
        <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" />
        <DxToolbar>
            <DxItem location="before" name="searchPanel" />
            <DxItem location="after" name="exportButton" />
        </DxToolbar>
        <DxExport :enabled="true" :formats="['xlsx', 'csv', 'pdf']" />
        <DxScrolling :useNative="true" />
    </DxDataGrid>
</div>
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps } from 'vue';
import { DxDataGrid, DxPaging, DxPager, DxFilterRow, DxHeaderFilter, DxEditing, DxSearchPanel, DxToolbar, DxItem, DxExport, DxScrolling } from 'devextreme-vue/data-grid';
import DataSource from 'devextreme/data/data_source';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { jsPDF } from 'jspdf';

const props = defineProps([ 'results' ]);
const gridComponent = ref(null);

const dataSource = new DataSource({
    store: {
        type: 'array',
        data: props.results,
        key: 'id'
    },
    paginate: true,
    pageSize: 10
});

const getDateCode = () => (new Date()).toISOString().substring(0, 10);

const onExporting = (e) => {
    const dateCode = getDateCode();
    const filename = `AI-Query-Results-${dateCode}`;

    if (e.format === 'xlsx') {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Query Results');
        exportDataGrid({
            component: e.component,
            worksheet: worksheet,
            autoFilterEnabled: true,
            customizeCell: function(options) {
                options.excelCell.font = { name: 'Arial', size: 11 };
                options.excelCell.alignment = { horizontal: 'left', vertical: 'center' };
            }
        }).then(function() {
            workbook.xlsx.writeBuffer().then(function(buffer) {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${filename}.xlsx`);
            });
        });
    } else if (e.format === 'pdf') {
        const doc = new jsPDF({ orientation: 'landscape' });
        exportDataGridToPdf({
            jsPDFDocument: doc,
            component: e.component,
            autoTableOptions: {
                headStyles: { fillColor: [66, 133, 244] },
                styles: { fontSize: 9 }
            }
        }).then(() => {
            doc.save(`${filename}.pdf`);
        });
    } else if (e.format === 'csv') {
        // Manual CSV export since DevExtreme doesn't support CSV natively
        const data = props.results;
        if (!data || data.length === 0) return;

        const columns = Object.keys(data[0]);
        const csvContent = [
            // Header row
            columns.map(col => escapeCSV(col)).join(','),
            // Data rows
            ...data.map(row => 
                columns.map(col => escapeCSV(row[col])).join(',')
            )
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `${filename}.csv`);
    }
};

const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Escape double quotes and wrap in quotes if contains comma, quote, or newline
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
};

</script>

<style lang="scss">
.chat-results-container {
    min-width: 400px;
    height: calc(100vh - 150px);
    max-height: 600px;
    box-sizing: border-box;
    
    .dx-datagrid {
        height: 100% !important;
        
        .dx-datagrid-header-panel {
            padding: 8px 0;
        }
    }
}

// Ensure the popup content area allows the grid to stretch
.dx-popup-wrapper .dx-popup-content:has(.chat-results-container) {
    padding: 12px;
}
</style>
